import { z } from 'zod';
import { TenantIdSchema } from '../types/common.types';

/**
 * AuthorizationGrant WIRE-contract (E-124, ADR-124) — a `@kgc/api-contract` single source.
 *
 * Miért itt: a `@kgc/auth-grant` (core) `dto.ts`-e `z.date()`-et használ (domain-réteg),
 * a JSON-wire viszont ISO-string dátum. A FE eddig kézzel duplikálta a típusokat
 * (`apps/kgc-web/src/api/auth-grants.ts`) → ez a framework-mentes, Zod-alapú wire-szerződés,
 * amit FE és BE közösen fogyaszt (workstream-ratify 2026-05-31, ~1 SP).
 *
 * NB: a create-input sémák a FE-FELÉ menő alakot tükrözik — a `createdBy`/`tenantId`
 * server-injected (auth-kontextusból), ezért NEM része a kliens-payloadnak.
 */

export const AuthorizationGrantTypeSchema = z.enum(['PERMANENT', 'ONE_TIME']);
export type AuthorizationGrantType = z.infer<typeof AuthorizationGrantTypeSchema>;

export const AuthorizationGrantStatusSchema = z.enum([
  'ACTIVE',
  'REVOKED',
  'EXPIRED',
  'SUPERSEDED',
]);
export type AuthorizationGrantStatus = z.infer<typeof AuthorizationGrantStatusSchema>;

/** ONE_TIME flow-nál a grant létrehozásakor rögzített átvevő-adatok (inline). */
export const AuthorizationGrantGranteeDataSchema = z.object({
  name: z.string().min(1).max(255),
  idCardNumber: z.string().max(64).optional(),
  address: z.string().max(500).optional(),
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  birthPlace: z.string().max(255).optional(),
  motherName: z.string().max(255).optional(),
  phone: z.string().max(64).optional(),
});
export type AuthorizationGrantGranteeData = z.infer<typeof AuthorizationGrantGranteeDataSchema>;

/** A teljes grant-entitás a wire-en (ISO-string dátumok). */
export const AuthorizationGrantSchema = z.object({
  id: z.string().uuid(),
  tenantId: TenantIdSchema,
  type: AuthorizationGrantTypeSchema,
  status: AuthorizationGrantStatusSchema,
  grantorPartnerId: z.string().uuid(),
  granteePartnerId: z.string().uuid().nullable(),
  granteeData: AuthorizationGrantGranteeDataSchema.nullable(),
  valueLimitHuf: z.number().nullable(),
  validFrom: z.string(),
  validUntil: z.string().nullable(),
  rentalId: z.string().uuid().nullable(),
  documentIds: z.array(z.string()),
  gracePeriodEndsAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedBy: z.string().nullable(),
  revocationReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
});
export type AuthorizationGrant = z.infer<typeof AuthorizationGrantSchema>;

/** PERMANENT grant kliens-payload (server adja a `createdBy`/`tenantId`-t). */
export const CreatePermanentGrantInputSchema = z.object({
  grantorPartnerId: z.string().uuid(),
  granteePartnerId: z.string().uuid(),
  valueLimitHuf: z.number().nonnegative().nullable().optional(),
  validFrom: z.string().optional(),
  validUntil: z.string().nullable().optional(),
  documentIds: z.array(z.string()).optional(),
});
export type CreatePermanentGrantInput = z.infer<typeof CreatePermanentGrantInputSchema>;

/** ONE_TIME grant kliens-payload — `documentIds` kötelező (ld. ADR-124 D-2 a BE-n). */
export const CreateOneTimeGrantInputSchema = z.object({
  grantorPartnerId: z.string().uuid(),
  granteeData: AuthorizationGrantGranteeDataSchema,
  valueLimitHuf: z.number().nonnegative().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  rentalId: z.string().uuid(),
  documentIds: z.array(z.string()),
  skipDocumentUpload: z.boolean().optional(),
  gracePeriodHours: z.number().int().min(1).max(24).optional(),
});
export type CreateOneTimeGrantInput = z.infer<typeof CreateOneTimeGrantInputSchema>;
